import {
    IExpressionResolver, IExpressionResolution, ResolutionErrorKind,
    failedExpressionResolution, successfulExpressionResolution, IResolutionError, IParsingError, ParsingErrorKind
} from 'app/model/expressions/expression-resolution';
import { Expression, ExpressionCategory, ExpressionFunction, IFunctionOverload, Argument, ScalarExpression, ExpressionType, functionExpression } from 'app/model/expressions/expression';
import { FunctionArgumentParser, IArgument, ArgumentKind } from 'app/model/expressions/function-argument-parser';
import { stringsAreEqualCaseInsensitive, numberOfLeadingWhitespace } from 'app/shared/utils/string.util';
import { getPropertyStack, matchPropertyStackWithSchema, findPattern, buildSubExpressionResolver, getInnerError } from 'app/model/expressions/expression-utils';
import { Dataset } from 'app/model/dataset/dataset';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Owned } from 'app/model/base/named';
import { VariablesResolver } from 'app/model/expressions/bracket-expression-resolvers/variables-resolver';
import { LinkedService } from '../linkedservice/linkedservice';

/**
 * A resolver capable of resolving expression functions.
 */
export class FunctionResolver implements IExpressionResolver {
    /**
     * Initializes a new instance of the FunctionResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _registeredExpressions The registered expressions to search within.
     * @param _expressionResolversCallback Supported expression resolvers used to resolve function arguments.
     */
    public constructor(private _entity: Owned, private readonly _registeredExpressions: Expression[], private readonly _expressionResolversCallback: () => IExpressionResolver[]) {
    }

    public isOfType(expression: string, ignoreStart?: boolean): boolean {
        expression = expression ? expression.trim() : '';
        const isVariable = new VariablesResolver(null, null).isOfType(expression, ignoreStart);
        if (isVariable) {
            return false;
        } else if (ignoreStart) {
            // don't bother about the starting @
            // the expression is a function if it starts with a name followed by a left paranthesis and ends with a right paranthesis
            return /^[\w]+\([\s\S]*$/.test(expression);
        } else {
            // the expression is a function if it starts with an @ followed by a name followed by a left paranthesis and ends with a right paranthesis
            return /^@[\w]+\([\s\S]*$/.test(expression);
        }
    }

    public resolve(expression: string, ignoreStart?: boolean): IExpressionResolution {

        if (this.isOfType(expression, ignoreStart)) {
            // this appears to be a function, extract the function name and parameter list
            expression = expression ? expression.trimRight() : '';

            const index = numberOfLeadingWhitespace(expression) + (!ignoreStart ? 1 : 0);
            const openingParanthesisIndex = findPattern(expression, '(', index);
            const functionName = expression.substring(index, openingParanthesisIndex);
            let isRecognizedFunction = true;

            // locate the function in our expression registry
            let registeredFunctionMatch = this._registeredExpressions?.find(
                (exp) => exp.category === ExpressionCategory.Function && stringsAreEqualCaseInsensitive(exp.syntax, functionName)) as ExpressionFunction;

            if (!registeredFunctionMatch) {
                isRecognizedFunction = false;

                // this could be a logic apps functions which was recently added, assume it is a function that can accept anything
                // and returns any
                registeredFunctionMatch = functionExpression(functionName, functionName, '', 'Other')
                    .args(new Argument('arg1', '', 'Any').optional().ofVariableLength()).returns('Any');
            }

            let closingParanthesisIndex: number;

            try {
                closingParanthesisIndex = this.findEnclosingParanthesis(expression, openingParanthesisIndex, 0);
            } catch (error) {
                return failedExpressionResolution(expression, { kind: ResolutionErrorKind.InvalidSyntax, parsingError: error });
            }

            const functionArguments = expression.substring(openingParanthesisIndex + 1, closingParanthesisIndex);

            try {
                // parse function arguments, if we took out the @, accomodate for that in the passed offset
                const functionArgList = new FunctionArgumentParser(
                    openingParanthesisIndex + 1,
                    buildSubExpressionResolver(this._entity, this._expressionResolversCallback)).parse(functionArguments);

                // match the arguments against the registered function expressions
                const matchingOverloads = (registeredFunctionMatch as ExpressionFunction).matchArgumentCount(functionArgList.length);

                if (matchingOverloads.length <= 0) {
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.InvalidArgumentCount,
                        details: {
                            func: registeredFunctionMatch.name,
                            argumentCount: functionArgList.length
                        }
                    }, registeredFunctionMatch);
                }

                let overloadMatch: IFunctionOverload;

                for (const overload of matchingOverloads) {
                    overloadMatch = overload;
                    let overloadArgIndex = 0;

                    // try to match arguments to parameters
                    // eslint-disable-next-line @typescript-eslint/prefer-for-of
                    for (let i = 0; i < functionArgList.length; ++i) {
                        if (!this.doesMatch(functionArgList[i], overloadMatch.args[overloadArgIndex])) {
                            overloadMatch = undefined;
                            break;
                        }

                        if (!overloadMatch.args[overloadArgIndex].variableLength) {
                            // only proceed to next function argument if the current function parameter is not of variable length
                            // e.g. concat takes an arbitrary number of arguments that all get mapped to a single function parameter
                            overloadArgIndex++;
                        }
                    }

                    if (overloadMatch) {
                        break;
                    }
                }

                if (overloadMatch) {
                    const matchedFuncExpression = functionExpression(
                        registeredFunctionMatch.name, registeredFunctionMatch.syntax, registeredFunctionMatch.description, registeredFunctionMatch.functionCategory)
                        .args(...overloadMatch.args)
                        .returns(overloadMatch.returnType, overloadMatch.subReturnType);

                    const functionExpressionResolution = this.resolveFunctionChainedProperties(
                        matchedFuncExpression, expression, buildSubExpressionResolver(this._entity, this._expressionResolversCallback), !ignoreStart ? 1 : 0);

                    if (!functionExpressionResolution.isValid) {
                        return functionExpressionResolution;
                    }

                    if (isRecognizedFunction) {
                        // success!
                        return functionExpressionResolution;
                    } else {
                        // treat the non registered function as a warning
                        return failedExpressionResolution(
                            expression,
                            { kind: ResolutionErrorKind.FunctionNotFound, details: { functionName } },
                            matchedFuncExpression,
                            undefined,
                            true);
                    }
                } else {
                    // could not find a matching overload
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.NoMatchingArguments,
                        details: {
                            func: registeredFunctionMatch.name,
                            arguments: this._concatArgumentTypes(functionArgList)
                        }
                    });
                }
            } catch (error) {
                const parsingError = error as IParsingError;
                // legacy: only console error for a real error other than a warning
                if (parsingError.resolution?.treatAsWarning !== true){
                    console.error(parsingError);
                }

                const innerError = getInnerError(parsingError);

                return innerError || failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.InvalidSyntax,
                    parsingError
                });
            }
        } else {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression });
        }
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError } {
        return { isApplicable: entity && entity.concept && (entity.concept instanceof Pipeline || entity.concept instanceof Dataset || entity.concept instanceof LinkedService) };
    }

    /**
     * Checks if the provided argument token can be passed as a parameter to the given exression function argument.
     * @param token The actual argument value.
     * @param argument The expression function's parameter.
     */
    private doesMatch(token: IArgument, argument: Argument): boolean {
        const tokenToArgumentMap = new Map<ArgumentKind, ExpressionType[]>();

        tokenToArgumentMap.set('BoolLiteral', ['Bool', 'Any']);
        tokenToArgumentMap.set('IntegerLiteral', ['Int', 'Float', 'Any']);
        tokenToArgumentMap.set('FloatLiteral', ['Float', 'Any']);
        tokenToArgumentMap.set('Object', ['Object', 'Any']);
        tokenToArgumentMap.set('StringLiteral', ['String', 'Any']);
        tokenToArgumentMap.set('Null', ['Any']);

        if (tokenToArgumentMap.has(token.kind)) {
            return tokenToArgumentMap.get(token.kind).includes(argument.type);
        } else {
            if (token.kind === 'Expression' && token.expressionResolution && token.expressionResolution.expression) {
                const expressionTypeAssignmentMap = new Map<ExpressionType, ExpressionType[]>();

                expressionTypeAssignmentMap.set('Bool', ['Bool', 'Any']);
                expressionTypeAssignmentMap.set('Int', ['Int', 'Float', 'Any']);
                expressionTypeAssignmentMap.set('Float', ['Float', 'Any']);
                expressionTypeAssignmentMap.set('Object', ['Object', 'Any']);
                expressionTypeAssignmentMap.set('String', ['String', 'Any']);
                expressionTypeAssignmentMap.set('SecureString', ['SecureString', 'String', 'Any']);
                expressionTypeAssignmentMap.set('Array', ['Array', 'Any']);

                if (token.expressionResolution.expression.category === ExpressionCategory.Function) {
                    const expressionFunction = (token.expressionResolution.expression as ExpressionFunction);

                    if (expressionFunction.specification.returnType === 'Any' || expressionTypeAssignmentMap.get(
                        expressionFunction.specification.returnType).includes(argument.type)) {
                        if (expressionFunction.specification.returnType === 'Array') {
                            // check that the array elements type matches
                            return expressionFunction.specification.subReturnType === 'Any'
                                || expressionTypeAssignmentMap.get(expressionFunction.specification.subReturnType).includes(argument.subType);
                        } else {
                            // function's return type matches the argument
                            return true;
                        }
                    } else {
                        // function's return type does not match the argument
                        return false;
                    }
                } else {
                    // scalar expression
                    const expressionReturnType = (token.expressionResolution.expression as ScalarExpression).returnType;
                    const expressionSubReturnType = (token.expressionResolution.expression as ScalarExpression).returnSubType || 'Any';

                    if (expressionReturnType === 'Any' || expressionTypeAssignmentMap.get(expressionReturnType).includes(argument.type)) {
                        return expressionReturnType === 'Array'
                            ? expressionSubReturnType === 'Any' || expressionTypeAssignmentMap.get(expressionSubReturnType).includes(argument.subType)
                            : true;
                    } else {
                        // scalar expression return type does not match the argument
                        return false;
                    }
                }
            } else if (token.kind === 'Array') {
                switch (argument.type) {
                    case 'Array':
                        return argument.subType && token.elements && token.elements.length > 0
                            ? this.doesMatch(token.elements[0], new Argument('element', '', argument.subType))
                            : true;
                    case 'Any':
                        // we can assign anything to any
                        return true;
                    default:
                        return false;
                }
            }
        }

        return false;
    }

    /**
     * Handles any dotted properties after the function call. E.g. @json('{ "name": "John" }').name
     * @param expressionFunction The expression function.
     * @param expression The expression string.
     * @param resolveSubExpression A callback invoked to resolve sub expressions.
     * @param offset Error position offset from the start of the string.
     * @returns The function property expression resolution.
     */
    private resolveFunctionChainedProperties(
        expressionFunction: ExpressionFunction,
        expression: string,
        resolveSubExpression: (expr: string) => IExpressionResolution,
        offset: number): IExpressionResolution {

        const closingParanthesisIndex = this.findEnclosingParanthesis(expression, findPattern(expression, '('), offset);

        // ensure we do not have extra trailing paranthesis
        if (/^[\) ]+$/.test(expression.substring(closingParanthesisIndex + 1))) {
            return failedExpressionResolution(expression, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError: {
                    startIndex: offset + closingParanthesisIndex + 1,
                    error: ParsingErrorKind.ExtraEnclosingParanthesis
                }
            });
        }

        const propertyChain = expression.substring(closingParanthesisIndex + 1);

        if (!propertyChain) {
            // no sub properties detected, the function is the final expression
            return successfulExpressionResolution(expression, expressionFunction);
        }

        if (!(['Any', 'Object', 'Array'].includes(expressionFunction.specification.returnType))) {
            // Check for built in arithmetic operators
            if (/\+|\-|\/|\*$/.test(propertyChain)) {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.UseBuiltInArithmetic,
                    startIndex: offset + closingParanthesisIndex + 1,
                    details: { propertyName: expressionFunction.name }
                });
            }
            // there are excess properties specified next to primitive returning function
            return failedExpressionResolution(expression, {
                kind: ResolutionErrorKind.ExcesssNestedProperties,
                startIndex: offset + closingParanthesisIndex + 1,
                details: { propertyName: expressionFunction.name }
            });
        }

        // get the nested properties
        let propertyNestings: Array<{ property: string, separator: string }>;

        try {
            propertyNestings = getPropertyStack(propertyChain, offset + closingParanthesisIndex + 1);
        } catch (parsingError) {
            return failedExpressionResolution(expression, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError
            });
        }

        const propertyExpressionResolution = matchPropertyStackWithSchema(
            propertyChain, propertyNestings, { expressionType: expressionFunction.specification.returnType }, offset + closingParanthesisIndex + 1, resolveSubExpression);

        if (propertyExpressionResolution.isValid) {
            return successfulExpressionResolution(expression,
                new ScalarExpression(
                    ExpressionCategory.Property,
                    expression,
                    expression,
                    expression,
                    (propertyExpressionResolution.expression as ScalarExpression).returnType,
                    (propertyExpressionResolution.expression as ScalarExpression).returnSubType));
        } else {
            return propertyExpressionResolution;
        }
    }

    private findEnclosingParanthesis(expression: string, openingParanthesisIndex: number, offset: number): number {
        let openParanthesisCount = 1;
        let index = openingParanthesisIndex;

        while (true) {
            const nextOpenParanthesisIndex = findPattern(expression, '(', index + 1);
            const nextClosingParanthesisIndex = findPattern(expression, ')', index + 1);

            const openingParanthesisFound = nextOpenParanthesisIndex > index;
            const closingParanthesisFound = nextClosingParanthesisIndex > index;

            if (!closingParanthesisFound) {
                const parsingError: IParsingError = {
                    error: ParsingErrorKind.MissingEnclosingParanthesis,
                    startIndex: offset + openingParanthesisIndex
                };

                throw parsingError;
            }

            if (openingParanthesisFound && nextOpenParanthesisIndex < nextClosingParanthesisIndex) {
                // we found a nested invocation
                openParanthesisCount++;
                index = nextOpenParanthesisIndex;
            } else {
                openParanthesisCount--;
                index = nextClosingParanthesisIndex;

                if (openParanthesisCount <= 0) {
                    return nextClosingParanthesisIndex;
                }
            }
        }
    }

    private _concatArgumentTypes(functionArgList: IArgument[]): string {
        let args;

        if (functionArgList) {
            args = functionArgList.map((arg): string => {
                return arg.expressionResolution ? arg.expressionResolution.returnType : arg.kind;
            })
                .join(', ');
        }

        return args;
    }
}
